/**
 * Dagdelen — canonieke vensters voor gedeeltelijke beschikbaarheid.
 *
 * SPIEGELBESTAND. Byte-identieke kopieen:
 *   1. apps/team-app/src/lib/dagdelen.ts              (schrijft de tijden)
 *   2. brands/cinco/apps/office-app/src/lib/dagdelen.ts (leest en plant erop)
 * Bewaakt door scripts/smoke-workforce.sh. Wijzig je er een, wijzig ze allebei.
 *
 * De medewerker kiest een dagdeel, de database slaat gewone tijden op
 * (availability.time_from / time_to). Het label is dus presentatie, de tijd is
 * de waarheid — grenzen bijstellen raakt geen bestaande rijen kapot, het
 * verandert alleen hoe nieuwe keuzes vertalen.
 *
 * Grenzen afgeleid uit de eigen roostertijden: routes starten vanaf 05:50,
 * wasserij loopt uit tot 20:41.
 *
 * LET OP: availability heeft UNIQUE(staff_id, date), dus een dag draagt EEN
 * venster. "Wel ochtend, wel avond, niet de middag" kan hier niet in.
 */

export type DagdeelKey = 'hele-dag' | 'ochtend' | 'middag' | 'avond';

export interface Dagdeel {
  key: DagdeelKey;
  label: string;
  /** null = hele dag: geen tijden wegschrijven, gedraagt zich als voorheen */
  from: string | null;
  to: string | null;
}

export const DAGDELEN: Dagdeel[] = [
  { key: 'hele-dag', label: 'Hele dag', from: null,    to: null },
  { key: 'ochtend',  label: 'Ochtend',  from: '07:00', to: '12:00' },
  { key: 'middag',   label: 'Middag',   from: '12:00', to: '17:00' },
  { key: 'avond',    label: 'Avond',    from: '17:00', to: '21:00' },
];

/** 'HH:MM:SS' en 'HH:MM' uit Postgres/UI gelijktrekken naar 'HH:MM'. */
export function toHHmm(v: string | null | undefined): string | null {
  if (!v) return null;
  return v.slice(0, 5);
}

/**
 * Welk dagdeel hoort bij een opgeslagen venster? Exacte match op de grenzen;
 * alles wat daar niet op valt is een handmatig venster en houdt zijn tijden.
 */
export function dagdeelVoorVenster(from: string | null, to: string | null): Dagdeel | null {
  const f = toHHmm(from);
  const t = toHHmm(to);
  if (!f && !t) return DAGDELEN[0]!;
  return DAGDELEN.find(d => d.from === f && d.to === t) ?? null;
}

/** Korte weergave voor een roostercel: 'Ochtend' of anders '15:00-19:00'. */
export function vensterLabel(from: string | null, to: string | null): string | null {
  const f = toHHmm(from);
  const t = toHHmm(to);
  if (!f && !t) return null;
  const d = dagdeelVoorVenster(from, to);
  if (d && d.key !== 'hele-dag') return d.label;
  if (f && t) return `${f}-${t}`;
  return f ? `vanaf ${f}` : `tot ${t}`;
}

/**
 * Snijd een beschikbaarheidsvenster met de tijden van een dienst.
 * Geeft null als er geen bruikbare overlap is. Zonder venster (hele dag)
 * blijft de dienst ongewijzigd — dat is het bestaande gedrag.
 */
export function snijVenster(
  dienstVan: string,
  dienstTot: string,
  availVan: string | null,
  availTot: string | null,
  minutenMinimaal = 120,
): { from: string; to: string } | null {
  const f = toHHmm(availVan);
  const t = toHHmm(availTot);
  if (!f && !t) return { from: toHHmm(dienstVan)!, to: toHHmm(dienstTot)! };

  const van = maxTijd(toHHmm(dienstVan)!, f ?? '00:00');
  const tot = minTijd(toHHmm(dienstTot)!, t ?? '23:59');
  if (minutenTussen(van, tot) < minutenMinimaal) return null;
  return { from: van, to: tot };
}

function naarMinuten(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}
function minutenTussen(a: string, b: string): number {
  return naarMinuten(b) - naarMinuten(a);
}
function maxTijd(a: string, b: string): string {
  return naarMinuten(a) >= naarMinuten(b) ? a : b;
}
function minTijd(a: string, b: string): string {
  return naarMinuten(a) <= naarMinuten(b) ? a : b;
}
